import domainModel from 'domain-model';
import _ from 'lodash';

import type { OrderFlowRuleReference } from 'domain-model/types/oms';

import { StateSection } from './StateSection';
import {
  Container,
  Row,
  Col,
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
} from 'reactstrap';
import type { OrderFlow, OrderFlowRule } from 'domain-model/types/oms';

const { orderFlowChecks }: { orderFlowChecks: OrderFlow[] } = domainModel.order;

interface OrderFlowConfiguratorProps {
  value: OrderFlowRuleReference[]; // Array of rule references to display
  name: string; // Name of the prop
  onChange: (name: string, newValue: OrderFlowRuleReference[]) => void; // Callback function
}

function getStandardRuleReferences(): OrderFlowRuleReference[] {
  return _.chain(orderFlowChecks)
    .flatMap((flow: OrderFlow) => flow.rules)
    .filter((rule: OrderFlowRule) => rule.isStandardRule)
    .map((rule: OrderFlowRule) => ({ id: rule.id }))
    .value();
}

export default function OrderFlowConfigurator({
  value,
  name,
  onChange,
}: OrderFlowConfiguratorProps) {
  const selectedRules = new Set(value ? value.map(ref => ref.id) : []);

  const updateSelectedRules = (ruleIds: string[], selected: boolean) => {
    ruleIds.forEach(ruleId => {
      if (selected) {
        selectedRules.add(ruleId);
      } else {
        selectedRules.delete(ruleId);
      }
    });

    const result = Array.from(selectedRules)
      .map(id => ({ id }))
      .filter(x => !_.isEmpty(x));

    onChange(name, result as OrderFlowRuleReference[]);
  };

  const handleRuleToggle = (ruleId: string, selected: boolean) => {
    updateSelectedRules([ruleId], selected);
  };

  const handleStateToggle = (state: string, selected: boolean) => {
    const stateFlow = orderFlowChecks.find(
      (flow: OrderFlow) => flow.state === state,
    );
    if (!stateFlow) return;

    updateSelectedRules(
      stateFlow.rules.map((rule: OrderFlowRule) => rule.id),
      selected,
    );
  };

  const handleSelectAllStandardRules = () => {
    onChange(name, getStandardRuleReferences());
  };

  const handleDeselectAll = () => {
    onChange(name, []);
  };

  const totalRules = orderFlowChecks.reduce(
    (sum: number, flow: OrderFlow) => sum + flow.rules.length,
    0,
  );
  const selectedCount = selectedRules.size;

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="bi bi-gear-fill fs-2 me-3"></i>
              <div>Konfigurera vilka regler som ska användas i orderflödet</div>
            </div>
            <div>
              <Badge color="secondary" className="fs-6">
                {selectedCount} av {totalRules} regler valda
              </Badge>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg="8">
          {orderFlowChecks.map((orderFlow: OrderFlow) => (
            <StateSection
              key={orderFlow.state}
              orderFlow={orderFlow}
              selectedRules={selectedRules}
              onRuleToggle={handleRuleToggle}
              onStateToggle={handleStateToggle}
            />
          ))}
        </Col>

        <Col lg="4">
          <div className="sticky-top" style={{ top: '1rem' }}>
            <Card className="mb-4">
              <CardHeader>
                <h5 className="card-title mb-0">Snabbåtgärder</h5>
              </CardHeader>
              <CardBody className="d-grid gap-2">
                <Button
                  color="primary"
                  outline
                  onClick={handleSelectAllStandardRules}
                >
                  Välj alla standardregler
                </Button>
                <Button color="secondary" outline onClick={handleDeselectAll}>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Avmarkera alla
                </Button>
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
